import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  LockKeyhole,
  Fingerprint,
  Server,
  CheckCircle2,
  ArrowUpRight,
} from "lucide-react";

const securityFeatures = [
  {
    icon: ShieldCheck,
    title: "Secure OTP Delivery",
    description:
      "Deliver verification codes through a secure and reliable messaging infrastructure built for critical authentication flows.",
  },
  {
    icon: LockKeyhole,
    title: "Encrypted Communication",
    description:
      "Protect sensitive authentication data with secure communication layers designed to keep verification activity protected.",
  },
  {
    icon: Fingerprint,
    title: "Identity Verification",
    description:
      "Strengthen user authentication with OTP-based verification designed to reduce unauthorized access and fraudulent activity.",
  },
  {
    icon: Server,
    title: "Reliable Infrastructure",
    description:
      "Built on scalable infrastructure that supports high-volume authentication requests without compromising reliability.",
  },
];

const securityPoints = [
  "Secure authentication workflows",
  "Fast OTP generation and delivery",
  "Reliable message processing",
  "Scalable enterprise infrastructure",
];

const fadeUp = (delay = 0) => ({
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      delay,
      ease: [0.22, 1, 0.36, 1],
    },
  },
});

const SecuritySection = () => {
  return (
    <section
      id="security"
      className="
        relative
        overflow-hidden
        bg-[var(--bg-primary)]
        px-4
        py-20
        font-saira
        sm:px-6
        sm:py-24
        lg:px-8
        lg:py-28
      "
    >
      {/* =====================================================
          BACKGROUND EFFECTS
      ====================================================== */}

      {/* Left Ambient Glow */}
      <motion.div
        animate={{
          x: [0, 35, 0],
          y: [0, -20, 0],
          scale: [1, 1.08, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="
          pointer-events-none
          absolute
          left-[-180px]
          top-[15%]
          h-[380px]
          w-[380px]
          rounded-full
          bg-orange-500/[0.07]
          blur-[130px]
        "
      />

      {/* Right Ambient Glow */}
      <motion.div
        animate={{
          x: [0, -30, 0],
          y: [0, 25, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="
          pointer-events-none
          absolute
          bottom-[5%]
          right-[-180px]
          h-[420px]
          w-[420px]
          rounded-full
          bg-orange-500/[0.05]
          blur-[150px]
        "
      />

      {/* Center Ambient Glow */}
      <motion.div
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.35, 0.55, 0.35],
        }}
        transition={{
          duration: 9,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="
          pointer-events-none
          absolute
          left-1/2
          top-1/2
          h-[450px]
          w-[450px]
          -translate-x-1/2
          -translate-y-1/2
          rounded-full
          bg-orange-500/[0.025]
          blur-[180px]
        "
      />

      {/* Small Floating Orb */}
      <motion.div
        animate={{
          y: [0, -25, 0],
          x: [0, 12, 0],
          opacity: [0.2, 0.5, 0.2],
        }}
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="
          pointer-events-none
          absolute
          left-[18%]
          top-[22%]
          h-2
          w-2
          rounded-full
          bg-orange-400
          shadow-[0_0_20px_rgba(249,115,22,0.8)]
        "
      />

      {/* Another Floating Orb */}
      <motion.div
        animate={{
          y: [0, 20, 0],
          opacity: [0.15, 0.45, 0.15],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
        className="
          pointer-events-none
          absolute
          right-[20%]
          top-[35%]
          h-1.5
          w-1.5
          rounded-full
          bg-orange-500
          shadow-[0_0_18px_rgba(249,115,22,0.8)]
        "
      />

      {/* =====================================================
          MAIN CONTAINER
      ====================================================== */}

      <div className="relative mx-auto max-w-7xl">
        {/* =====================================================
            HEADING
        ====================================================== */}

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.2,
          }}
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.12,
              },
            },
          }}
          className="mx-auto mb-14 max-w-3xl text-center"
        >
          {/* Badge */}
          <motion.div variants={fadeUp(0)}>
            <motion.div
              whileHover={{
                scale: 1.04,
                boxShadow: "0 0 30px rgba(249,115,22,0.10)",
              }}
              transition={{ duration: 0.25 }}
              className="
                mb-5
                inline-flex
                cursor-default
                items-center
                gap-2
                rounded-full
                border
                border-orange-500/20
                bg-orange-500/[0.06]
                px-4
                py-2
              "
            >
              <motion.div
                animate={{
                  rotate: [0, -8, 8, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <ShieldCheck
                  className="h-4 w-4 text-orange-400"
                  strokeWidth={2}
                />
              </motion.div>

              <span
                className="
                  text-[11px]
                  font-semibold
                  uppercase
                  tracking-[0.16em]
                  text-orange-400
                  sm:text-xs
                "
              >
                Enterprise Security
              </span>
            </motion.div>
          </motion.div>

          {/* Heading */}
          <motion.h2
            variants={fadeUp(0.1)}
            className="
              text-3xl
              font-bold
              leading-[1.08]
              tracking-[-0.04em]
              text-[var(--text-primary)]
              sm:text-4xl
              md:text-5xl
              lg:text-[52px]
            "
          >
            Security Built Into{" "}
            <motion.span
              animate={{
                textShadow: [
                  "0 0 0px rgba(249,115,22,0)",
                  "0 0 25px rgba(249,115,22,0.18)",
                  "0 0 0px rgba(249,115,22,0)",
                ],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="text-orange-500"
            >
              Every OTP
            </motion.span>
          </motion.h2>

          {/* Description */}
          <motion.p
            variants={fadeUp(0.2)}
            className="
              mx-auto
              mt-5
              max-w-2xl
              text-sm
              leading-7
              text-[var(--text-secondary)]
              sm:text-base
              sm:leading-8
            "
          >
            Keep your authentication workflows secure, reliable, and ready
            for scale with infrastructure designed for modern digital
            businesses.
          </motion.p>
        </motion.div>

        {/* =====================================================
            MAIN SECURITY LAYOUT
        ====================================================== */}

        <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
          {/* =====================================================
              LEFT SECURITY PANEL
          ====================================================== */}

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.15,
            }}
            variants={fadeUp(0.08)}
            whileHover={{
              y: -4,
              transition: {
                duration: 0.3,
              },
            }}
            className="
              group
              relative
              overflow-hidden
              rounded-[28px]
              border
              border-[var(--border-primary)]
              bg-[var(--card-bg)]
              p-6
              transition-all
              duration-500
              hover:border-orange-500/20
              hover:shadow-[0_20px_70px_rgba(0,0,0,0.35)]
              sm:p-8
              lg:p-9
            "
          >
            {/* Animated Border Glow */}
            <motion.div
              animate={{
                opacity: [0.15, 0.35, 0.15],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="
                pointer-events-none
                absolute
                inset-0
                rounded-[28px]
                border
                border-orange-500/10
              "
            />

            {/* Top Glow */}
            <motion.div
              animate={{
                scale: [1, 1.15, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="
                pointer-events-none
                absolute
                right-[-80px]
                top-[-80px]
                h-56
                w-56
                rounded-full
                bg-orange-500/[0.08]
                blur-[80px]
              "
            />

            <div className="relative">
              {/* Shield Icon */}
              <motion.div
                animate={{
                  y: [0, -5, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                whileHover={{
                  scale: 1.08,
                  rotate: 5,
                }}
                className="
                  mb-7
                  flex
                  h-14
                  w-14
                  items-center
                  justify-center
                  rounded-2xl
                  border
                  border-orange-500/20
                  bg-orange-500/[0.08]
                  transition-all
                  duration-300
                  group-hover:border-orange-500/35
                  group-hover:bg-orange-500/[0.12]
                  group-hover:shadow-[0_0_30px_rgba(249,115,22,0.10)]
                "
              >
                <ShieldCheck
                  className="h-7 w-7 text-orange-500"
                  strokeWidth={1.8}
                />
              </motion.div>

              {/* Heading */}
              <h3
                className="
                  max-w-md
                  text-2xl
                  font-bold
                  leading-tight
                  tracking-tight
                  text-[var(--text-primary)]
                  sm:text-3xl
                "
              >
                Protect every verification request with confidence.
              </h3>

              {/* Description */}
              <p
                className="
                  mt-4
                  max-w-lg
                  text-sm
                  leading-7
                  text-[var(--text-secondary)]
                  sm:text-base
                "
              >
                From OTP generation to delivery and verification, every
                stage of your authentication journey is designed around
                reliability and security.
              </p>

              {/* Security Checklist */}
              <div className="mt-8 space-y-4">
                {securityPoints.map((point, index) => (
                  <motion.div
                    key={point}
                    initial={{
                      opacity: 0,
                      x: -20,
                    }}
                    whileInView={{
                      opacity: 1,
                      x: 0,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      duration: 0.5,
                      delay: 0.25 + index * 0.1,
                      ease: "easeOut",
                    }}
                    whileHover={{
                      x: 5,
                      transition: {
                        duration: 0.2,
                      },
                    }}
                    className="
                      flex
                      cursor-default
                      items-center
                      gap-3
                    "
                  >
                    <motion.div
                      whileHover={{
                        scale: 1.15,
                      }}
                    >
                      <CheckCircle2
                        className="
                          h-[18px]
                          w-[18px]
                          shrink-0
                          text-orange-500
                        "
                        strokeWidth={2}
                      />
                    </motion.div>

                    <span
                      className="
                        text-sm
                        text-[var(--text-secondary)]
                      "
                    >
                      {point}
                    </span>
                  </motion.div>
                ))}
              </div>

              {/* Bottom Status */}
              <motion.div
                whileHover={{
                  borderColor: "rgba(249,115,22,0.20)",
                  backgroundColor:
                    "color-mix(in srgb, var(--text-primary) 3.5%, transparent)",
                }}
                className="
                  mt-9
                  flex
                  items-center
                  justify-between
                  gap-4
                  rounded-2xl
                  border
                  border-[var(--border-secondary)]
                  bg-[color-mix(in_srgb,var(--text-primary)_2.5%,transparent)]
                  px-4
                  py-4
                  transition-all
                  duration-300
                "
              >
                <div className="flex items-center gap-3">
                  {/* Animated Status Dot */}
                  <span className="relative flex h-2.5 w-2.5">
                    <motion.span
                      animate={{
                        scale: [1, 1.8, 1],
                        opacity: [0.5, 0, 0.5],
                      }}
                      transition={{
                        duration: 1.8,
                        repeat: Infinity,
                        ease: "easeOut",
                      }}
                      className="
                        absolute
                        inline-flex
                        h-full
                        w-full
                        rounded-full
                        bg-orange-500
                      "
                    />

                    <span
                      className="
                        relative
                        inline-flex
                        h-2.5
                        w-2.5
                        rounded-full
                        bg-orange-500
                        shadow-[0_0_12px_rgba(249,115,22,0.7)]
                      "
                    />
                  </span>

                  <div>
                    <p
                      className="
                        text-xs
                        font-semibold
                        text-[var(--text-primary)]
                      "
                    >
                      Infrastructure Protected
                    </p>

                    <p
                      className="
                        mt-0.5
                        text-[11px]
                        text-[var(--text-muted)]
                      "
                    >
                      Always-on authentication layer
                    </p>
                  </div>
                </div>

                <motion.div
                  animate={{
                    x: [0, 3, 0],
                    y: [0, -2, 0],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <ArrowUpRight className="h-5 w-5 text-orange-500" />
                </motion.div>
              </motion.div>
            </div>
          </motion.div>

          {/* =====================================================
              RIGHT FEATURE CARDS
          ====================================================== */}

          <div className="grid gap-4 sm:grid-cols-2">
            {securityFeatures.map((feature, index) => {
              const Icon = feature.icon;

              return (
                <motion.div
                  key={feature.title}
                  initial={{
                    opacity: 0,
                    y: 35,
                    scale: 0.97,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                  }}
                  viewport={{
                    once: true,
                    amount: 0.15,
                  }}
                  transition={{
                    duration: 0.65,
                    delay: 0.12 + index * 0.1,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  whileHover={{
                    y: -7,
                    transition: {
                      duration: 0.25,
                      ease: "easeOut",
                    },
                  }}
                  className="
                    group
                    relative
                    overflow-hidden
                    rounded-[24px]
                    border
                    border-[var(--border-primary)]
                    bg-[var(--card-bg)]
                    p-6
                    transition-all
                    duration-500
                    hover:border-orange-500/25
                    hover:shadow-[0_18px_50px_rgba(0,0,0,0.35)]
                    sm:p-7
                  "
                >
                  {/* Animated Card Shine */}
                  <motion.div
                    initial={{
                      x: "-120%",
                    }}
                    whileHover={{
                      x: "120%",
                    }}
                    transition={{
                      duration: 0.8,
                      ease: "easeInOut",
                    }}
                    className="
                      pointer-events-none
                      absolute
                      inset-y-0
                      left-0
                      w-1/2
                      skew-x-[-20deg]
                      bg-gradient-to-r
                      from-transparent
                      via-[color-mix(in_srgb,var(--text-primary)_3.5%,transparent)]
                      to-transparent
                    "
                  />

                  {/* Hover Glow */}
                  <div
                    className="
                      pointer-events-none
                      absolute
                      right-[-50px]
                      top-[-50px]
                      h-32
                      w-32
                      rounded-full
                      bg-orange-500/0
                      blur-[55px]
                      transition-all
                      duration-500
                      group-hover:bg-orange-500/[0.10]
                    "
                  />

                  <div className="relative">
                    {/* Feature Icon */}
                    <motion.div
                      whileHover={{
                        scale: 1.08,
                        rotate: 5,
                      }}
                      transition={{
                        duration: 0.25,
                      }}
                      className="
                        mb-6
                        flex
                        h-12
                        w-12
                        items-center
                        justify-center
                        rounded-xl
                        border
                        border-[var(--border-primary)]
                        bg-[color-mix(in_srgb,var(--text-primary)_3.5%,transparent)]
                        transition-all
                        duration-300
                        group-hover:border-orange-500/25
                        group-hover:bg-orange-500/[0.08]
                        group-hover:shadow-[0_0_25px_rgba(249,115,22,0.08)]
                      "
                    >
                      <Icon
                        className="
                          h-5
                          w-5
                          text-orange-500
                          transition-transform
                          duration-300
                        "
                        strokeWidth={1.8}
                      />
                    </motion.div>

                    {/* Title */}
                    <h3
                      className="
                        text-lg
                        font-semibold
                        tracking-tight
                        text-[var(--text-primary)]
                      "
                    >
                      {feature.title}
                    </h3>

                    {/* Description */}
                    <p
                      className="
                        mt-3
                        text-sm
                        leading-6
                        text-[var(--text-muted)]
                        transition-colors
                        duration-300
                        group-hover:text-[var(--text-secondary)]
                      "
                    >
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SecuritySection;